import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material';
import TakeShapeLogo from 'app/common/components/logo/takeshape-logo';
import { ServiceIcon } from 'app/common/components/service-list';
import { branding } from 'app/config';
import { findProvider } from 'app/services/providers';
import { type HTMLAttributes, type JSXElementConstructor, useMemo } from 'react';
import { filterOptions } from '../autocomplete/match-sorter-filter-options.js';
import { ListboxComponent } from '../autocomplete/react-window-listbox.js';
import Widget from '../widget.js';
import type { QueryOption, useSelectQuery } from './select-query-hook.js';

export type SelectQueryProps = ReturnType<typeof useSelectQuery>;

const getProviderAvatar = (providerName: string) => {
  if (providerName === 'takeshape') {
    return (
      <ServiceIcon key={providerName} title={branding.productName}>
        <TakeShapeLogo />
      </ServiceIcon>
    );
  }

  const provider = findProvider(providerName);
  if (provider) {
    return <ServiceIcon key={providerName} src={provider.avatarPath} title={provider.title} />;
  }

  return null;
};

const renderOption = (attributes: HTMLAttributes<HTMLLIElement>, option: QueryOption) => {
  const { namespace, provider, name } = option;

  return (
    <li {...attributes} data-testid={`select-query-${name}`}>
      <Box display="flex" alignItems="center" sx={{ gap: 2, width: '100%' }}>
        {getProviderAvatar(provider)}
        <Box display="flex" flexDirection="column" sx={{ overflow: 'hidden' }}>
          {namespace && (
            <Typography color="textSecondary" variant="caption" style={{ lineHeight: 1.2 }}>
              {namespace}
            </Typography>
          )}
          <Typography style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{name}</Typography>
        </Box>
      </Box>
    </li>
  );
};

const getOptionLabel = (option: QueryOption) => {
  return option?.name ?? '';
};

const isOptionEqualToValue = (option: QueryOption, value: QueryOption) => {
  return option.ref === value.ref;
};

export function SelectQuery(props: SelectQueryProps) {
  const { options, handleChange, value, disabled, id, label, isRequired } = props;

  const startAdornment = useMemo(() => {
    return value ? getProviderAvatar(value.provider) : null;
  }, [value]);

  return (
    <Widget testId={id}>
      <Autocomplete<QueryOption, false, false, false>
        value={value}
        onChange={handleChange}
        options={options}
        ListboxComponent={ListboxComponent as JSXElementConstructor<any>}
        filterOptions={filterOptions(['name'])}
        renderOption={renderOption}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={label}
            required={isRequired}
            inputProps={{
              ...params.inputProps,
              'data-testid': `${id}__input`
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>}
                  {params.InputProps.startAdornment}
                </>
              )
            }}
          />
        )}
      />
    </Widget>
  );
}
